import { FC, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useStore } from '../../hooks/useStore';
import { supportEmail } from '../../misc/globals';
import { Loading } from '../../lib/components';
import { useTranslation } from 'react-i18next';
import { LoginLogo } from './partials/LoginLogo';
import { Box, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { ApiError } from '@pxtra-client-lib-legacy/gen';
import { FeedbackAlert } from '../../lib/components/atomic/feedback';
import { Button } from '../../lib/components/atomic/button';

const MAX_WIDTH = '400px';

type Props = {
  provider: 'google' | 'microsoft';
};

export const OauthLogin: FC<Props> = ({ provider }) => {
  const [searchParams] = useSearchParams();
  const { authenticationStore } = useStore();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const authorizationCode = searchParams.get('code');

  const handleContactSupport = () => {
    const subject = encodeURIComponent('Custom Benefit Zuweisung');
    const body = encodeURIComponent(t('custom:contact_mail_intro'));
    window.open(`mailto:${supportEmail}?subject=${subject}&body=${body}`, '_blank');
  };

  const getErrorContent = (error: ApiError | Error) => {
    let errorType = 'UNKNOWN';

    if ('body' in error && error.body) {
      const errorMessage =
        error.body.errors?.non_field_errors?.[0] ??
        error.body.non_field_errors?.[0] ??
        error.body.message ??
        '';

      if (errorMessage.includes('Single Sign On is not enabled for your company')) {
        errorType = 'DEACTIVATED';
      } else if (errorMessage.includes('User could not be authenticated')) {
        errorType = 'NO_ACCOUNT';
      }
    }

    return {
      title: t(`company:SSO_ERROR_${errorType}_TITLE`),
      description: t(`company:SSO_ERROR_${errorType}_DESCRIPTION`, {
        returnObjects: true
      }) as string[]
    };
  };

  const oauthLoginMutation = useMutation({
    mutationFn: async (code: string) => {
      if (provider === 'google') {
        return await authenticationStore.loginWithGoogle(code);
      } else if (provider === 'microsoft') {
        return await authenticationStore.loginWithMicrosoft(code);
      }
      throw new Error('Invalid provider');
    },
    onSuccess: () => {
      navigate('/');
    }
  });

  const errorContent = oauthLoginMutation.error ? getErrorContent(oauthLoginMutation.error) : null;

  useEffect(() => {
    if (authorizationCode) {
      oauthLoginMutation.mutate(authorizationCode);
    } else {
      navigate('/login');
    }
  }, [authorizationCode]);

  useEffect(() => {
    const browserLanguage = navigator.language || navigator.languages?.[0] || 'en';
    const targetLanguage = browserLanguage.toLowerCase().startsWith('de') ? 'de' : 'en';
    i18n.changeLanguage(targetLanguage);
  }, []);

  return (
    <Box
      sx={{
        minHeight: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Box
        sx={{
          backgroundColor: 'white',
          borderRadius: '24px',
          padding: '0 32px 64px 32px',
          margin: '84px auto',
          maxWidth: '500px',
          width: '100%',
          textAlign: 'center'
        }}
      >
        <LoginLogo />
        {oauthLoginMutation.isPending && (
          <>
            <Loading />
            <Typography className="mt-4 mb-10">{t('company:SSO_LOGIN_LOADING')}</Typography>
          </>
        )}
        {oauthLoginMutation.isError && errorContent && (
          <Box
            sx={{
              maxWidth: MAX_WIDTH,
              margin: '0 auto',
              '& .MuiInput-root': {
                borderRadius: '16px'
              }
            }}
          >
            <Box sx={{ textAlign: 'left', marginBottom: '24px' }}>
              <FeedbackAlert
                title={errorContent.title}
                description={errorContent.description}
                severity="error"
              />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <Button variant="primary" onClick={() => navigate('/login')}>
                {t('company:SSO_BACK')}
              </Button>
              <Button variant="tertiary" onClick={handleContactSupport}>
                {t('custom:contact_support')}
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};
